import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { APP_API_BASE } from "../../../config/api";
import { useAuth } from "../../../auth/useAuth";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") ||
  `${APP_API_BASE}`;

const POLL_INTERVAL_MS = 30000;
const ONLINE_THRESHOLD_SECONDS = 5 * 60;
const STALE_THRESHOLD_SECONDS = 60 * 60;

const formatNumber = (value, unit = "") => {
  if (value === null || value === undefined || value === "") return "Not reported";
  const num = Number(value);
  if (!Number.isFinite(num)) return "Not reported";
  const rounded = Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/\.00$/, "");
  return unit ? `${rounded} ${unit}` : rounded;
};

const severityTone = (severity) => {
  const key = String(severity || "").toLowerCase();
  if (key === "critical") return { fg: "#B42318", bg: "#FEF2F2" };
  if (key === "warning") return { fg: "#B45309", bg: "#FFF7ED" };
  if (key === "normal") return { fg: "#166534", bg: "#ECFDF3" };
  return { fg: "#475569", bg: "#F1F5F9" };
};

const prettifyMetricKey = (key) => {
  const text = String(key || "").trim();
  if (!text) return "Unknown";
  return text
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const isReported = (value) => value !== null && value !== undefined && String(value).trim() !== "";

const formatAnyValue = (value, unit = "") => {
  if (!isReported(value)) return "Not reported";

  if (typeof value === "boolean") return value ? "True" : "False";

  const numericValue = Number(value);
  if (Number.isFinite(numericValue) && String(value).trim() !== "") {
    return formatNumber(numericValue, unit);
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }

  return String(value);
};

const buildFallbackDerivedKpis = (payload) => {
  const p = payload?.primary_kpis || {};
  const signalQuality = payload?.connectivity_summary?.signal_quality?.label || null;
  const operationalHealth = payload?.operational_health?.status || null;
  const connectivity = payload?.connectivity?.status || null;
  const duplicateRows = payload?.data_quality?.duplicate_rows ?? null;

  return [
    {
      id: "kpi_pump_runtime_total_seconds",
      label: "Pump Runtime (Total)",
      formula: "latest(pump_run_seconds_total)",
      source_keys: ["pump_run_seconds_total"],
      unit: "sec",
      value: p.pump_runtime_total_seconds ?? null,
      section: "pump_summary",
      status: isReported(p.pump_runtime_total_seconds) ? "available" : "unavailable",
    },
    {
      id: "kpi_pump_runtime_window_seconds",
      label: "Pump Runtime (Window)",
      formula: "counter_delta(pump_run_seconds_total)",
      source_keys: ["pump_run_seconds_total"],
      unit: "sec",
      value: p.pump_runtime_window_seconds ?? null,
      section: "primary_kpis",
      status: isReported(p.pump_runtime_window_seconds) ? "available" : "unavailable",
    },
    {
      id: "kpi_water_volume_total_l",
      label: "Water Volume (Total)",
      formula: "latest(water_volume_l_total)",
      source_keys: ["water_volume_l_total"],
      unit: "L",
      value: p.water_volume_total_l ?? null,
      section: "water_summary",
      status: isReported(p.water_volume_total_l) ? "available" : "unavailable",
    },
    {
      id: "kpi_water_volume_window_l",
      label: "Water Volume (Window)",
      formula: "counter_delta(water_volume_l_total)",
      source_keys: ["water_volume_l_total"],
      unit: "L",
      value: p.water_volume_window_l ?? null,
      section: "primary_kpis",
      status: isReported(p.water_volume_window_l) ? "available" : "unavailable",
    },
    {
      id: "kpi_estimated_flow_lpm",
      label: "Estimated Flow",
      formula: "latest(estimated_flow_lpm)",
      source_keys: ["estimated_flow_lpm"],
      unit: "LPM",
      value: p.estimated_flow_lpm ?? null,
      section: "water_summary",
      status: isReported(p.estimated_flow_lpm) ? "available" : "unavailable",
    },
    {
      id: "kpi_rated_discharge_lpm",
      label: "Rated Discharge",
      formula: "latest(rated_discharge_lpm)",
      source_keys: ["rated_discharge_lpm"],
      unit: "LPM",
      value: p.rated_discharge_lpm ?? null,
      section: "water_summary",
      status: isReported(p.rated_discharge_lpm) ? "available" : "unavailable",
    },
    {
      id: "kpi_signal_quality_label",
      label: "Signal Quality",
      formula: "classify_signal(rssi_dbm, rsrp_dbm, sinr_db)",
      source_keys: ["rssi_dbm", "rsrp_dbm", "sinr_db"],
      value: signalQuality,
      section: "connectivity_summary",
      status: isReported(signalQuality) ? "available" : "unavailable",
    },
    {
      id: "kpi_operational_health_status",
      label: "Operational Health",
      formula: "fault_precedence(device_health, overcurrent, dry_run, tamper, freshness)",
      source_keys: ["device_health", "overcurrent", "dry_run", "tamper"],
      value: operationalHealth,
      section: "operational_health",
      status: isReported(operationalHealth) ? "available" : "unavailable",
    },
    {
      id: "kpi_connectivity_status",
      label: "Connectivity State",
      formula: "freshness(last_timestamp, now)",
      source_keys: ["timestamp"],
      value: connectivity,
      section: "connectivity",
      status: isReported(connectivity) ? "available" : "unavailable",
    },
    {
      id: "kpi_duplicate_packet_count",
      label: "Duplicate Packet Count",
      formula: "total_rows - unique_rows",
      source_keys: ["timestamp", "server_time"],
      unit: "rows",
      value: duplicateRows,
      section: "data_quality",
      status: isReported(duplicateRows) ? "available" : "unavailable",
    },
  ];
};

const normalizeParameterCatalog = (payload, derivedKpis) => {
  if (Array.isArray(payload?.parameter_catalog) && payload.parameter_catalog.length) {
    return payload.parameter_catalog;
  }

  const latestData = payload?.latest_telemetry?.data;
  if (!latestData || typeof latestData !== "object") {
    return [];
  }

  const parameterToDerived = new Map();
  (derivedKpis || []).forEach((kpi) => {
    (kpi?.source_keys || []).forEach((sourceKey) => {
      if (!parameterToDerived.has(sourceKey)) {
        parameterToDerived.set(sourceKey, []);
      }
      parameterToDerived.get(sourceKey).push(kpi.id);
    });
  });

  return Object.keys(latestData)
    .sort((a, b) => a.localeCompare(b))
    .map((key) => {
      const value = latestData[key];
      const linkedKpiIds = parameterToDerived.get(key) || [];
      return {
        key,
        label: prettifyMetricKey(key),
        category: "unclassified",
        category_label: "Unclassified",
        unit: null,
        value_type: typeof value,
        value_raw: value,
        value,
        status: isReported(value) ? "reported" : "not_reported",
        in_latest_payload: true,
        observed_count: isReported(value) ? 1 : 0,
        last_reported_at: payload?.latest_telemetry?.timestamp || null,
        derived_kpi_ids: linkedKpiIds,
        derived_kpi_labels: linkedKpiIds
          .map((id) => (derivedKpis || []).find((item) => item.id === id)?.label)
          .filter(Boolean),
      };
    });
};

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const toBool = (value) => {
  if (typeof value === "boolean") return value;
  if (value === null || value === undefined || value === "") return null;

  const normalized = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "y", "on"].includes(normalized)) return true;
  if (["false", "0", "no", "n", "off"].includes(normalized)) return false;
  return null;
};

const stableStringifyFlat = (record) => {
  const normalized = Object.keys(record || {})
    .sort()
    .reduce((acc, key) => {
      acc[key] = record[key];
      return acc;
    }, {});
  return JSON.stringify(normalized);
};

const dedupeRows = (rows) => {
  const map = new Map();

  (rows || []).forEach((row) => {
    const data = row?.data && typeof row.data === "object" ? row.data : row;
    const key = `${row?.timestamp || ""}|${stableStringifyFlat(data)}`;
    const nextServer = Date.parse(row?.server_time || "") || 0;

    const current = map.get(key);
    if (!current) {
      map.set(key, row);
      return;
    }

    const currentServer = Date.parse(current?.server_time || "") || 0;
    if (nextServer >= currentServer) {
      map.set(key, row);
    }
  });

  return [...map.values()].sort(
    (a, b) => (Date.parse(a?.timestamp || "") || 0) - (Date.parse(b?.timestamp || "") || 0),
  );
};

const counterDelta = (rows, key) => {
  let delta = 0;
  let previous = null;

  rows.forEach((row) => {
    const data = row?.data && typeof row.data === "object" ? row.data : row;
    const current = toNumber(data?.[key]);
    if (current === null) return;

    if (previous === null) {
      previous = current;
      return;
    }

    if (current >= previous) delta += current - previous;
    else delta += Math.max(current, 0);

    previous = current;
  });

  return Number(delta.toFixed(3));
};

const downsamplePoints = (points, maxPoints = 120) => {
  if (!Array.isArray(points) || points.length <= maxPoints) return points || [];
  const step = Math.ceil(points.length / maxPoints);
  const sampled = points.filter((_, index) => index % step === 0);
  if (sampled[sampled.length - 1] !== points[points.length - 1]) {
    sampled.push(points[points.length - 1]);
  }
  return sampled;
};

const buildSeries = (rows, key) => {
  const points = (rows || [])
    .map((row) => {
      const data = row?.data && typeof row.data === "object" ? row.data : row;
      const value = toNumber(data?.[key]);
      const timestamp = row?.timestamp || row?.server_time;
      if (value === null || !timestamp) return null;

      return { ts: timestamp, value: Number(value.toFixed(3)) };
    })
    .filter(Boolean);

  return downsamplePoints(points, 120);
};

const resolveFreshness = (lastSeenIso) => {
  const lastSeenMs = Date.parse(lastSeenIso || "");
  if (!Number.isFinite(lastSeenMs)) {
    return {
      status: "never_seen",
      label: "Offline (never seen)",
      is_live: false,
      age_seconds: null,
    };
  }

  const ageSeconds = Math.max(0, Math.round((Date.now() - lastSeenMs) / 1000));
  if (ageSeconds <= ONLINE_THRESHOLD_SECONDS) {
    return { status: "online", label: "Online", is_live: true, age_seconds: ageSeconds };
  }

  if (ageSeconds <= STALE_THRESHOLD_SECONDS) {
    return {
      status: "stale",
      label: "Stale (last known values)",
      is_live: false,
      age_seconds: ageSeconds,
    };
  }

  return {
    status: "offline",
    label: "Offline",
    is_live: false,
    age_seconds: ageSeconds,
  };
};

const resolveSignalQuality = (rssiDbm, rsrpDbm, sinrDb) => {
  const rssi = toNumber(rssiDbm);
  const rsrp = toNumber(rsrpDbm);
  const sinr = toNumber(sinrDb);

  if (rssi === null && rsrp === null && sinr === null) {
    return { label: "Unknown", severity: "unknown", reason: "No signal metrics reported" };
  }

  if ((rssi !== null && rssi > 0) || (rsrp !== null && rsrp > 0)) {
    return {
      label: "Unknown",
      severity: "unknown",
      reason: "Signal values outside expected dBm range",
    };
  }

  if ((rsrp === null || rsrp >= -95) && (sinr === null || sinr >= 10) && (rssi === null || rssi >= -70)) {
    return { label: "Excellent", severity: "normal", reason: null };
  }

  if ((rsrp === null || rsrp >= -105) && (sinr === null || sinr >= 3) && (rssi === null || rssi >= -85)) {
    return { label: "Good", severity: "normal", reason: null };
  }

  if ((rsrp === null || rsrp >= -115) && (sinr === null || sinr >= 0) && (rssi === null || rssi >= -100)) {
    return { label: "Fair", severity: "warning", reason: null };
  }

  return { label: "Poor", severity: "critical", reason: null };
};

const buildFallbackPayload = ({ device, rows }) => {
  const totalRows = Array.isArray(rows) ? rows.length : 0;
  const dedupedRows = dedupeRows(rows || []);
  const latestRow = dedupedRows[dedupedRows.length - 1] || null;
  const latestData = latestRow?.data && typeof latestRow.data === "object" ? latestRow.data : latestRow || {};

  const overcurrent = toBool(latestData?.overcurrent) === true;
  const dryRun = toBool(latestData?.dry_run) === true;
  const tamper = toBool(latestData?.tamper) === true;

  const freshness = resolveFreshness(device?.last_seen || latestRow?.timestamp);

  const healthStatus = overcurrent || dryRun || tamper
    ? { status: "critical", label: "Critical", severity: "critical" }
    : freshness.status === "online"
      ? { status: "normal", label: "Normal", severity: "normal" }
      : { status: "unknown", label: "Unknown", severity: "unknown" };

  const activeAlarms = [];
  if (overcurrent) activeAlarms.push({ id: "alarm-overcurrent", title: "Overcurrent", severity: "critical", status: "active" });
  if (dryRun) activeAlarms.push({ id: "alarm-dry-run", title: "Dry Run", severity: "critical", status: "active" });
  if (tamper) activeAlarms.push({ id: "alarm-tamper", title: "Tamper", severity: "critical", status: "active" });

  const buildTransitions = (key, title, normalize) => {
    const events = [];
    let previous = null;

    dedupedRows.forEach((row) => {
      const data = row?.data && typeof row.data === "object" ? row.data : row;
      const current = normalize(data?.[key]);
      if (current === null || current === undefined || current === "") return;

      if (previous !== null && current !== previous) {
        events.push({
          ts: row?.timestamp || row?.server_time,
          type: key,
          severity: "info",
          message: `${title} changed from ${previous} to ${current}.`,
        });
      }

      previous = current;
    });

    return events;
  };

  const recentEvents = [
    ...buildTransitions("grid_state", "Grid", (value) => (value ? String(value).toUpperCase() : null)),
    ...buildTransitions("battery_backup_active", "Battery backup", (value) => {
      const boolValue = toBool(value);
      if (boolValue === null) return null;
      return boolValue ? "ON" : "OFF";
    }),
  ]
    .filter((event) => event?.ts)
    .sort((a, b) => (Date.parse(b.ts || "") || 0) - (Date.parse(a.ts || "") || 0))
    .slice(0, 30);

  const signalQuality = resolveSignalQuality(latestData?.rssi_dbm, latestData?.rsrp_dbm, latestData?.sinr_db);

  return {
    device: {
      id: device?.id || null,
      name: device?.name || null,
      device_type_id: device?.device_type_id || null,
      device_type_name: device?.device_type?.name || null,
      is_active: device?.is_active !== false,
      last_seen: device?.last_seen || latestRow?.timestamp || null,
    },
    connectivity: {
      ...freshness,
      age_minutes: freshness.age_seconds !== null ? Number((freshness.age_seconds / 60).toFixed(2)) : null,
    },
    operational_health: {
      ...healthStatus,
      reason: overcurrent || dryRun || tamper ? "Critical fault condition is active." : null,
      faults: [
        ...(overcurrent ? ["Overcurrent alarm is active"] : []),
        ...(dryRun ? ["Dry-run alarm is active"] : []),
        ...(tamper ? ["Tamper alarm is active"] : []),
      ],
    },
    latest_telemetry: {
      timestamp: latestRow?.timestamp || null,
      server_time: latestRow?.server_time || null,
      packet_type: latestData?.packet_type || "telemetry",
      data: latestData,
      deduped: true,
    },
    primary_kpis: {
      pump_runtime_total_seconds: toNumber(latestData?.pump_run_seconds_total),
      pump_runtime_window_seconds: counterDelta(dedupedRows, "pump_run_seconds_total"),
      water_volume_total_l: toNumber(latestData?.water_volume_l_total),
      water_volume_window_l: counterDelta(dedupedRows, "water_volume_l_total"),
      estimated_flow_lpm: toNumber(latestData?.estimated_flow_lpm),
      rated_discharge_lpm: toNumber(latestData?.rated_discharge_lpm),
      pump_state: latestData?.pump_state || null,
      grid_state: latestData?.grid_state || null,
    },
    active_alarms: activeAlarms,
    recent_events: recentEvents,
    connectivity_summary: {
      signal_quality: signalQuality,
      rssi_dbm: toNumber(latestData?.rssi_dbm),
      rsrp_dbm: toNumber(latestData?.rsrp_dbm),
      sinr_db: toNumber(latestData?.sinr_db),
      cache_pending_count: toNumber(latestData?.cache_pending_count),
      cache_replayed: toBool(latestData?.cache_replayed),
      rtc_synced: toBool(latestData?.rtc_synced),
    },
    data_quality: {
      total_rows: totalRows,
      unique_rows: dedupedRows.length,
      duplicate_rows: Math.max(0, totalRows - dedupedRows.length),
      dedupe_applied: true,
      negative_ingestion_latency_count: dedupedRows.filter((row) => {
        const ts = Date.parse(row?.timestamp || "");
        const server = Date.parse(row?.server_time || "");
        return Number.isFinite(ts) && Number.isFinite(server) && server < ts;
      }).length,
    },
    chart_series: {
      estimated_flow_lpm: buildSeries(dedupedRows, "estimated_flow_lpm"),
      water_volume_l_total: buildSeries(dedupedRows, "water_volume_l_total"),
      pump_run_seconds_total: buildSeries(dedupedRows, "pump_run_seconds_total"),
      current_r_a: buildSeries(dedupedRows, "current_r_a"),
    },
  };
};

const ChartBlock = ({ title, points, unit }) => {
  if (!Array.isArray(points) || points.length < 2) {
    return (
      <Card sx={{ borderRadius: 4, border: "1px solid rgba(148,163,184,0.2)" }}>
        <CardContent>
          <Typography sx={{ fontWeight: 800, color: "#0F172A", mb: 1 }}>{title}</Typography>
          <Typography sx={{ color: "#64748B", fontSize: 13 }}>
            No data is available for the selected period.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 4, border: "1px solid rgba(148,163,184,0.2)" }}>
      <CardContent>
        <Typography sx={{ fontWeight: 800, color: "#0F172A", mb: 1 }}>{title}</Typography>
        <Box sx={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 12, right: 8, left: -18, bottom: 2 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.28)" />
              <XAxis dataKey="ts" tick={{ fontSize: 11, fill: "#94A3B8" }} tickFormatter={(value) => String(value).slice(11, 19)} />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} />
              <Tooltip
                labelFormatter={(value) => String(value)}
                formatter={(value) => [formatNumber(value, unit), "Value"]}
              />
              <Line type="monotone" dataKey="value" stroke="#2563EB" strokeWidth={2.6} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default function WSMSDeviceDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { deviceId } = useParams();
  const { activeOrg } = useAuth();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [isVisible, setIsVisible] = useState(
    typeof document === "undefined" ? true : document.visibilityState === "visible",
  );

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const handleVisibility = () => setIsVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const fetchDashboard = useCallback(
    async (silent = false, signal) => {
      if (!deviceId) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const headers = {
        Authorization: `Bearer ${token}`,
        ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
      };

      try {
        if (!silent) setLoading(true);
        else setRefreshing(true);

        setError("");
        const response = await axios.get(`${API_BASE}/api/v1/devices/${deviceId}/dashboards/wsms`, {
          headers,
          params: { hours: 48, limit: 5000 },
          signal,
        });

        setPayload(response.data || null);
        return;
      } catch (err) {
        if (axios.isCancel?.(err) || err?.name === "CanceledError") return;
      }

      try {
        const stateDevice = location.state?.device || null;
        let device = stateDevice;

        if (!device) {
          const deviceRes = await axios.get(`${API_BASE}/api/v1/devices/${deviceId}`, {
            headers,
            signal,
          });
          device = deviceRes.data || { id: deviceId };
        }

        let telemetryRows = [];
        const stateRows = Array.isArray(stateDevice?.telemetryRows) ? stateDevice.telemetryRows : null;
        if (stateRows && stateRows.length) {
          telemetryRows = stateRows.map((row) => ({
            timestamp: row?.timestamp,
            server_time: row?.server_time,
            data: Object.fromEntries(
              Object.entries(row || {}).filter(([key]) => key !== "timestamp" && key !== "server_time"),
            ),
          }));
        } else {
          const telemetryRes = await axios.get(`${API_BASE}/api/v1/devices/${deviceId}/telemetry`, {
            headers,
            params: { limit: 2000, include_total: "false" },
            signal,
          });

          telemetryRows = Array.isArray(telemetryRes.data?.data)
            ? telemetryRes.data.data.map((row) => ({
                timestamp: row?.timestamp,
                server_time: row?.server_time,
                data: row?.data || {},
              }))
            : [];
        }

        const fallbackPayload = buildFallbackPayload({ device, rows: telemetryRows });
        setPayload(fallbackPayload);
        setError("WSMS endpoint unavailable. Showing compatibility view from telemetry data.");
      } catch (fallbackError) {
        if (axios.isCancel?.(fallbackError) || fallbackError?.name === "CanceledError") return;
        setError("Unable to load WSMS dashboard data. Please verify backend API and organization access.");
      } finally {
        if (!silent) setLoading(false);
        else setRefreshing(false);
      }
    },
    [deviceId, activeOrg?.id, location.state?.device],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchDashboard(false, controller.signal);
    return () => controller.abort();
  }, [fetchDashboard]);

  useEffect(() => {
    if (!isVisible) return undefined;

    const timer = setInterval(() => {
      const controller = new AbortController();
      fetchDashboard(true, controller.signal);
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [fetchDashboard, isVisible]);

  const healthTone = severityTone(payload?.operational_health?.severity);
  const connectionTone = severityTone(
    payload?.connectivity?.status === "online"
      ? "normal"
      : payload?.connectivity?.status === "stale"
        ? "warning"
        : "critical",
  );

  const latestDataEntries = useMemo(() => {
    const data = payload?.latest_telemetry?.data;
    if (!data || typeof data !== "object") return [];
    return Object.entries(data);
  }, [payload?.latest_telemetry?.data]);

  const derivedKpis = useMemo(() => {
    if (Array.isArray(payload?.derived_kpis) && payload.derived_kpis.length) {
      return payload.derived_kpis;
    }
    return buildFallbackDerivedKpis(payload);
  }, [payload]);

  const derivedKpiById = useMemo(() => {
    return derivedKpis.reduce((acc, item) => {
      if (item?.id) {
        acc[item.id] = item;
      }
      return acc;
    }, {});
  }, [derivedKpis]);

  const parameterCatalog = useMemo(() => {
    return normalizeParameterCatalog(payload, derivedKpis);
  }, [payload, derivedKpis]);

  const kpis = useMemo(() => {
    const p = payload?.primary_kpis || {};
    const dq = payload?.data_quality || {};

    return [
      { label: "Pump State", value: p.pump_state || "Not reported" },
      { label: "Grid State", value: p.grid_state || "Not reported" },
      { label: "Runtime (Total)", value: formatNumber(p.pump_runtime_total_seconds, "sec") },
      { label: "Runtime (Window)", value: formatNumber(p.pump_runtime_window_seconds, "sec") },
      { label: "Water (Total)", value: formatNumber(p.water_volume_total_l, "L") },
      { label: "Water (Window)", value: formatNumber(p.water_volume_window_l, "L") },
      { label: "Estimated Flow", value: formatNumber(p.estimated_flow_lpm, "LPM") },
      { label: "Deduplicated Rows", value: `${dq.unique_rows ?? 0} / ${dq.total_rows ?? 0}` },
    ];
  }, [payload]);

  const activeAlarms = payload?.active_alarms || [];
  const events = payload?.recent_events || [];

  return (
    <Box
      sx={{
        minHeight: "100%",
        py: 1,
        pt: { xs: 5, sm: 5, md: 3 },
        pb: 4,
        px: { xs: 2, md: 3 },
        background:
          "radial-gradient(circle at top left, rgba(14,165,233,0.14), transparent 30%), radial-gradient(circle at top right, rgba(16,185,129,0.12), transparent 28%), linear-gradient(180deg, #F8FBFF 0%, #EEF4F8 100%)",
      }}
    >
      <Box sx={{ maxWidth: 1440, mx: "auto" }}>
        <Card
          sx={{
            borderRadius: 5,
            border: "1px solid rgba(15,23,42,0.08)",
            boxShadow: "0 20px 45px rgba(15,23,42,0.08)",
            mb: 2,
          }}
        >
          <CardContent>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between">
              <Box>
                <Button
                  startIcon={<ArrowBackRoundedIcon />}
                  onClick={() => navigate(-1)}
                  sx={{ textTransform: "none", borderRadius: 999, mb: 1 }}
                >
                  Back
                </Button>
                <Typography sx={{ fontSize: { xs: 22, md: 30 }, fontWeight: 900, color: "#0F172A" }}>
                  WSMS Device Operations
                </Typography>
                <Typography sx={{ color: "#64748B", mt: 0.5 }}>
                  {payload?.device?.name || location.state?.device?.name || deviceId}
                </Typography>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                <Chip
                  label={payload?.connectivity?.label || "Unknown"}
                  sx={{ fontWeight: 800, color: connectionTone.fg, bgcolor: connectionTone.bg }}
                />
                <Chip
                  label={payload?.operational_health?.label || "Unknown"}
                  sx={{ fontWeight: 800, color: healthTone.fg, bgcolor: healthTone.bg }}
                />
              </Stack>
            </Stack>

            {loading ? <LinearProgress sx={{ mt: 1.5 }} /> : null}
            {refreshing ? <LinearProgress sx={{ mt: 1.5 }} color="inherit" /> : null}

            {payload?.connectivity?.status === "stale" || payload?.connectivity?.status === "offline" ? (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                Showing last known values. Live status-sensitive KPIs are marked as unknown until fresh telemetry arrives.
              </Alert>
            ) : null}

            {error ? (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                {error}
              </Alert>
            ) : null}
          </CardContent>
        </Card>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
            gap: 1.5,
            mb: 2,
          }}
        >
          {kpis.map((item) => (
            <Card key={item.label} sx={{ borderRadius: 4, border: "1px solid rgba(148,163,184,0.18)" }}>
              <CardContent>
                <Typography sx={{ color: "#64748B", fontSize: 12, fontWeight: 700 }}>{item.label}</Typography>
                <Typography sx={{ color: "#0F172A", fontSize: 24, fontWeight: 900, mt: 0.6 }}>
                  {item.value}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", xl: "1.2fr 1fr" },
            gap: 1.5,
            mb: 2,
          }}
        >
          <Card sx={{ borderRadius: 4, border: "1px solid rgba(148,163,184,0.2)" }}>
            <CardContent>
              <Typography sx={{ fontWeight: 800, color: "#0F172A" }}>Active Alarms</Typography>
              <Typography sx={{ color: "#64748B", fontSize: 13, mb: 1.2 }}>
                Overcurrent and safety faults override generic healthy status.
              </Typography>

              {!activeAlarms.length ? (
                <Alert severity="success">No active alarms.</Alert>
              ) : (
                <Stack spacing={1}>
                  {activeAlarms.map((alarm) => (
                    <Box
                      key={alarm.id}
                      sx={{
                        p: 1.2,
                        borderRadius: 2.5,
                        border: "1px solid rgba(248,113,113,0.35)",
                        background: "rgba(254,242,242,0.72)",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <WarningAmberRoundedIcon fontSize="small" sx={{ color: "#B42318" }} />
                        <Typography sx={{ fontWeight: 800, color: "#B42318" }}>{alarm.title}</Typography>
                      </Stack>
                      <Typography sx={{ fontSize: 12.5, color: "#7F1D1D", mt: 0.5 }}>
                        {alarm.message || "Active fault condition"}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 4, border: "1px solid rgba(148,163,184,0.2)" }}>
            <CardContent>
              <Typography sx={{ fontWeight: 800, color: "#0F172A" }}>Connectivity & Data Quality</Typography>
              <Divider sx={{ my: 1.2 }} />
              <Stack spacing={0.9}>
                <Typography sx={{ fontSize: 13, color: "#334155" }}>
                  Signal quality: <b>{payload?.connectivity_summary?.signal_quality?.label || "Unknown"}</b>
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#334155" }}>
                  RSSI: {formatNumber(payload?.connectivity_summary?.rssi_dbm, "dBm")}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#334155" }}>
                  RSRP: {formatNumber(payload?.connectivity_summary?.rsrp_dbm, "dBm")}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#334155" }}>
                  SINR: {formatNumber(payload?.connectivity_summary?.sinr_db, "dB")}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#334155" }}>
                  Duplicate rows removed: {payload?.data_quality?.duplicate_rows ?? 0}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#334155" }}>
                  Negative latency flags: {payload?.data_quality?.negative_ingestion_latency_count ?? 0}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        <Card sx={{ borderRadius: 4, border: "1px solid rgba(148,163,184,0.2)", mb: 2 }}>
          <CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 800, color: "#0F172A" }}>Derived KPI Registry</Typography>
              <Chip
                size="small"
                label={`${derivedKpis.length} KPIs`}
                sx={{ bgcolor: "#EFF6FF", color: "#1E40AF", fontWeight: 700 }}
              />
            </Stack>
            {!derivedKpis.length ? (
              <Typography sx={{ color: "#64748B", fontSize: 13 }}>No derived KPI definitions available.</Typography>
            ) : (
              <Box sx={{ maxHeight: 320, overflowY: "auto", pr: 0.5 }}>
                {derivedKpis.map((kpi, index) => {
                  const tone = severityTone(kpi?.status === "unavailable" ? "unknown" : "normal");
                  return (
                    <Box
                      key={kpi.id || `${kpi.label}-${index}`}
                      sx={{
                        py: 1,
                        borderBottom:
                          index === derivedKpis.length - 1
                            ? "none"
                            : "1px solid rgba(241,245,249,0.9)",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", md: "center" }}
                      >
                        <Box>
                          <Typography sx={{ color: "#0F172A", fontWeight: 800, fontSize: 13 }}>{kpi.label || kpi.id}</Typography>
                          <Typography sx={{ color: "#64748B", fontSize: 12 }}>{kpi.formula || "Not configured"}</Typography>
                        </Box>
                        <Stack direction="row" spacing={0.7} alignItems="center" sx={{ flexWrap: "wrap" }}>
                          <Chip
                            size="small"
                            label={formatAnyValue(kpi.value, kpi.unit || "")}
                            sx={{ bgcolor: tone.bg, color: tone.fg, fontWeight: 700 }}
                          />
                          {(kpi.source_keys || []).length ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={(kpi.source_keys || []).join(", ")}
                              sx={{ borderColor: "rgba(148,163,184,0.5)", color: "#475569", maxWidth: { xs: "100%", md: 380 } }}
                            />
                          ) : null}
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 4, border: "1px solid rgba(148,163,184,0.2)", mb: 2 }}>
          <CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 800, color: "#0F172A" }}>Telemetry Parameter Coverage</Typography>
              <Stack direction="row" spacing={0.8}>
                <Chip
                  size="small"
                  label={`${parameterCatalog.length} parameters`}
                  sx={{ bgcolor: "#F0FDF4", color: "#166534", fontWeight: 700 }}
                />
                <Chip
                  size="small"
                  label={`${parameterCatalog.filter((item) => item?.status === "reported").length} reported`}
                  sx={{ bgcolor: "#EFF6FF", color: "#1E40AF", fontWeight: 700 }}
                />
              </Stack>
            </Stack>

            {!parameterCatalog.length ? (
              <Typography sx={{ color: "#64748B", fontSize: 13 }}>No telemetry parameters available.</Typography>
            ) : (
              <Box sx={{ maxHeight: 560, overflowY: "auto", pr: 0.5 }}>
                {parameterCatalog.map((parameter, index) => {
                  const linkedLabels = (parameter?.derived_kpi_labels || []).length
                    ? parameter.derived_kpi_labels
                    : (parameter?.derived_kpi_ids || [])
                        .map((id) => derivedKpiById[id]?.label || id)
                        .filter(Boolean);

                  return (
                    <Box
                      key={parameter.key || `parameter-${index}`}
                      sx={{
                        py: 0.9,
                        borderBottom:
                          index === parameterCatalog.length - 1
                            ? "none"
                            : "1px solid rgba(241,245,249,0.9)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gap: 1,
                          alignItems: "center",
                          gridTemplateColumns: { xs: "1fr", lg: "1.5fr 1fr 0.8fr 1fr 1.5fr" },
                        }}
                      >
                        <Box>
                          <Typography sx={{ color: "#0F172A", fontSize: 13, fontWeight: 800 }}>
                            {parameter.label || prettifyMetricKey(parameter.key)}
                          </Typography>
                          <Typography sx={{ color: "#64748B", fontSize: 12 }}>{parameter.key}</Typography>
                        </Box>
                        <Typography sx={{ color: "#0F172A", fontSize: 13, fontWeight: 700 }}>
                          {formatAnyValue(parameter?.value ?? parameter?.value_raw, parameter?.unit || "")}
                        </Typography>
                        <Typography sx={{ color: "#64748B", fontSize: 12 }}>
                          {parameter?.category_label || parameter?.category || "Unclassified"}
                        </Typography>
                        <Typography sx={{ color: "#64748B", fontSize: 12 }}>
                          {parameter?.last_reported_at || payload?.latest_telemetry?.timestamp || "Not reported"}
                        </Typography>
                        <Typography sx={{ color: "#475569", fontSize: 12 }}>
                          {linkedLabels.length ? linkedLabels.join(", ") : "No derived KPI dependency"}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
            gap: 1.5,
            mb: 2,
          }}
        >
          <ChartBlock title="Estimated Flow (LPM)" points={payload?.chart_series?.estimated_flow_lpm || []} unit="LPM" />
          <ChartBlock title="Water Total (L)" points={payload?.chart_series?.water_volume_l_total || []} unit="L" />
          <ChartBlock title="Pump Runtime Total (sec)" points={payload?.chart_series?.pump_run_seconds_total || []} unit="sec" />
          <ChartBlock title="Phase R Current (A)" points={payload?.chart_series?.current_r_a || []} unit="A" />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" },
            gap: 1.5,
          }}
        >
          <Card sx={{ borderRadius: 4, border: "1px solid rgba(148,163,184,0.2)" }}>
            <CardContent>
              <Typography sx={{ fontWeight: 800, color: "#0F172A", mb: 1 }}>Recent Events</Typography>
              {!events.length ? (
                <Typography sx={{ color: "#64748B", fontSize: 13 }}>No state transitions in selected window.</Typography>
              ) : (
                <Stack spacing={0.9}>
                  {events.slice(0, 20).map((event, index) => (
                    <Box
                      key={`${event.ts}-${event.type}-${index}`}
                      sx={{ p: 1, borderRadius: 2, bgcolor: "#F8FAFC", border: "1px solid rgba(226,232,240,0.9)" }}
                    >
                      <Typography sx={{ fontSize: 12, color: "#64748B" }}>{event.ts}</Typography>
                      <Typography sx={{ fontSize: 13, color: "#0F172A", fontWeight: 700 }}>{event.message}</Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 4, border: "1px solid rgba(148,163,184,0.2)" }}>
            <CardContent>
              <Typography sx={{ fontWeight: 800, color: "#0F172A", mb: 1 }}>Raw Telemetry Snapshot</Typography>
              {!latestDataEntries.length ? (
                <Typography sx={{ color: "#64748B", fontSize: 13 }}>No telemetry has been received.</Typography>
              ) : (
                <Box sx={{ maxHeight: 520, overflowY: "auto", pr: 0.5 }}>
                  {latestDataEntries.map(([key, value], index) => (
                    <Stack
                      key={key}
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                      sx={{
                        py: 0.7,
                        borderBottom:
                          index === latestDataEntries.length - 1
                            ? "none"
                            : "1px solid rgba(241,245,249,0.9)",
                      }}
                    >
                      <Typography sx={{ color: "#475569", fontWeight: 700, fontSize: 13 }}>{key}</Typography>
                      <Typography sx={{ color: "#0F172A", fontSize: 13, textAlign: "right" }}>
                        {value === "" || value === null || value === undefined ? "Not reported" : String(value)}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}
